import { useCallback, useEffect, useState } from "react";

type PermissionState = "default" | "denied" | "granted" | "unsupported";
type SoundVariant = "default" | "success" | "error" | "info";

let audioCtx: AudioContext | null = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AC =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AC) audioCtx = new AC();
  }
  return audioCtx;
}

export function primeNotificationSounds() {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  // silent tick to warm up the graph
  const osc = c.createOscillator();
  const gain = c.createGain();
  gain.gain.value = 0;
  osc.connect(gain).connect(c.destination);
  osc.start();
  osc.stop(c.currentTime + 0.01);
}

function playBeep(variant: SoundVariant = "success") {
  const c = ensureCtx();
  if (!c || c.state !== "running") return;

  const make = (freq: number, dur = 0.25, type: OscillatorType = "sine") => {
    const osc = c.createOscillator();
    const g = c.createGain();
    const now = c.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.25, now + 0.02); // attack
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur); // decay
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(g).connect(c.destination);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  };

  switch (variant) {
    case "success":
      make(784, 0.1, "triangle"); // high blip
      setTimeout(() => make(988, 0.1, "triangle"), 120);
      break;
    case "error":
      make(150, 0.3, "square"); // low buzzy tone
      setTimeout(() => make(120, 0.3, "square"), 320);
      break;
    case "info":
      make(600, 0.15, "sawtooth");
      break;
    default:
      make(440, 0.15, "sine");
  }
}

type NotificationOptionsWithSound = NotificationOptions & {
  sound?: SoundVariant | boolean;
};

export function useNotifications() {
  const [permission, setPermission] = useState<PermissionState>(() =>
    "Notification" in window ? Notification.permission : "unsupported"
  );

  useEffect(() => {
    if (!("Notification" in window)) setPermission("unsupported");
  }, []);

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      setPermission("unsupported");
      return "unsupported" as const;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const showNotification = useCallback(
    (title: string, options?: NotificationOptionsWithSound) => {
      if (!("Notification" in window))
        throw new Error("Notifications unsupported");
      if (Notification.permission !== "granted")
        throw new Error("Permission not granted");

      const n = new Notification(title, options);
      const wantSound =
        options?.silent === true
          ? false
          : options?.sound === undefined
            ? true
            : options.sound !== false;

      if (wantSound) {
        const variant: SoundVariant =
          typeof options?.sound === "string" ? options.sound : "default";
        playBeep(variant);
      }

      return n;
    },
    []
  );

  return {
    permission,
    requestPermission,
    showNotification,
    primeNotificationSounds,
  };
}
